import React, { useCallback, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Autocomplete, Button, ClickAwayListener, Popper, TextField, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Routes } from "constants/routes";
import { useNavigate } from "hooks";
import { partition } from "lodash";
import {
  useDealIdSelector,
  useDealSubAssetsSelector,
  useDealTitleSelector,
  useParentDealSelect,
  useTaskDealsSelect
} from "store";
import { DealBase, RoutesKeys } from "store/types";

export const Name: React.FC = () => {
  const dealTitle = useDealTitleSelector();
  const dealId = useDealIdSelector();
  const subAssets = useDealSubAssetsSelector();
  const parent = useParentDealSelect();
  const deals = useTaskDealsSelect();
  const { pathname } = useLocation();

  const dealsList = useMemo<Array<DealBase & { shifted: boolean }>>(() => {
    const parentDealId = parent?.id || dealId;
    const subDealIds = parent ? [dealId] : subAssets.map((subAsset) => subAsset.id);
    const ids = [parentDealId, ...subDealIds];
    const [family, others] = partition(deals, (deal) => ids.includes(deal.id));
    const familyDeals = ids.map((id) => family.find((deal) => deal.id === id)!);

    const parentDeal = { ...familyDeals[0], shifted: false };
    const childDeals = familyDeals.slice(1).map((deal) => ({ ...deal, shifted: true }));
    const otherDeals = others.map((deal) => ({ ...deal, shifted: false }));

    return [parentDeal, ...childDeals, ...otherDeals];
  }, [deals, subAssets, dealId, parent]);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const onChange = useCallback(
    (option: DealBase | null) => {
      if (!option) return;
      let route: RoutesKeys = "deal";

      if (matchPath(pathname, Routes.dealDashboard)) {
        route = "dealDashboard";
      }

      if (matchPath(pathname, Routes.dealMonitoring)) {
        route = "dealMonitoring";
      }

      if (matchPath(pathname, Routes.dealTaskManage)) {
        route = "dealTaskManage";
      }

      if (matchPath(pathname, Routes.dealReport)) {
        route = "dealReport";
      }

      if (matchPath(pathname, Routes.dealAmortization)) {
        route = "dealAmortization";
      }

      navigate({ route, params: { deal_id: option.id }, setQueriesMethod: "drop" });
    },
    [navigate, pathname]
  );

  return (
    <>
      <MaterialTooltip isShow placement="top" title={dealTitle}>
        <Button
          onClick={handleClick}
          sx={{
            minWidth: "87px",
            padding: "0 10px",
            backgroundColor: "#edf1f9",
            minHeight: "28px",
            maxHeight: "28px"
          }}
          endIcon={open ? <ArrowDropUpIcon fontSize="xl" /> : <ArrowDropDownIcon fontSize="xl" />}
        >
          <Typography
            sx={{
              maxWidth: "240px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "16px"
            }}
          >
            {dealTitle || "Loading..."}
          </Typography>
        </Button>
      </MaterialTooltip>
      <Popper placement="bottom-start" sx={{ zIndex: 50 }} open={open} anchorEl={anchorEl}>
        <ClickAwayListener onClickAway={handleClose}>
          <Autocomplete
            disablePortal
            options={dealsList}
            open
            popupIcon={""}
            onChange={(_, option) => onChange(option)}
            getOptionLabel={(option) => option.title}
            renderOption={(props, { id, title, shifted }) => {
              return (
                <li {...props} style={shifted ? { paddingLeft: "32px" } : {}} key={id}>
                  {title}
                </li>
              );
            }}
            PopperComponent={CustomPopper}
            sx={{
              border: "none",
              width: 250,
              background: "white",
              boxShadow: "-2px 2px 15px -2px rgba(0, 0, 0, 0.25);",
              borderRadius: "10px 10px 0 0"
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search a deal..."
                sx={{ "& fieldset": { border: "none" } }}
                autoFocus
              />
            )}
          />
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const CustomPopper = (props: any) => {
  const { children, ...other } = props;

  return (
    <Popper
      {...other}
      sx={{
        borderTop: "2px solid #bac7ce",
        maxHeight: 250,
        minWidth: 250,
        overflow: "hidden",
        boxShadow: "4px 10px 10px -7px rgba(0, 0, 0, 0.25)",
        borderRadius: "0 0 10px 10px",
        zIndex: 5
      }}
    >
      {children}
    </Popper>
  );
};
